import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Code, Plus, Warning, CheckCircle, XCircle, Clock, Pause, PencilSimple, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import HelpLink from "@/components/HelpLink";
import { TrackingTestModal } from "@/components/tracking/TrackingTestModal";
import { TrackingHealthBadge } from "@/components/tracking/TrackingHealthBadge";
import { TrackingGuideCard, TRACKING_EXAMPLE } from "@/components/tracking/TrackingGuideCard";

/**
 * Iter-56: Media Buyer custom tracking requests.
 *
 * Lets a Media Buyer request custom tracking snippets (RedTrack /
 * Voluum / Binom / Hyros / ClickFlare / other DSP or attribution
 * platforms) for a specific custom landing page. Every snippet is
 * INERT until an admin holding `landing_pages.approve_custom_tracking`
 * approves it — the buyer cannot self-approve or edit approved code
 * in place.
 */

const PLACEMENTS = [
  { value: "head", label: "<head>" },
  { value: "body_start", label: "<body> start" },
  { value: "body_end", label: "<body> end (before </body>)" },
  { value: "thank_you_only", label: "Thank-You page only" },
];

const TRIGGERS = [
  { value: "page_view", label: "Page view" },
  { value: "form_start", label: "Form start (first field typed in)" },
  { value: "form_submit_success", label: "Lead submit success" },
  { value: "thank_you_page", label: "Thank-You page loaded" },
  { value: "custom_event", label: "Custom internal event" },
  { value: "all_events", label: "All events (form start, lead submit, thank you) — landing + thank-you pages" },
];

const STATUS_BADGE = {
  draft:    { label: "DRAFT",             cls: "bg-zinc-100 border-zinc-500 text-zinc-800", icon: PencilSimple },
  pending:  { label: "PENDING APPROVAL",  cls: "bg-amber-100 border-amber-600 text-amber-900", icon: Clock },
  approved: { label: "APPROVED",          cls: "bg-emerald-100 border-emerald-700 text-emerald-900", icon: CheckCircle },
  rejected: { label: "REJECTED",          cls: "bg-red-100 border-red-700 text-red-900", icon: XCircle },
  disabled: { label: "DISABLED",          cls: "bg-zinc-200 border-zinc-700 text-zinc-800", icon: Pause },
};


export default function MediaBuyerCustomTracking() {
  const [requests, setRequests] = useState([]);
  const [killSwitch, setKillSwitch] = useState(false);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);         // request row being edited
  const [reviewingId, setReviewingId] = useState(null); // request row being viewed
  const [testing, setTesting] = useState(null);         // snippet being tested

  const testRow = async (id) => {
    try {
      const { data } = await api.get(`/custom-tracking/mine/${id}`);
      setTesting(data.request);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load the code"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, pg] = await Promise.all([
        api.get(`/custom-tracking/mine${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`),
        api.get("/media-buyer/landing-pages"),
      ]);
      setRequests(r.data.requests || []);
      setKillSwitch(Boolean(r.data.kill_switch?.enabled));
      setPages((pg.data.pages || []).filter((p) => !p.is_sample_page));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (row) => { setEditing(row); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const submitFor = async (id) => {
    try {
      await api.post(`/custom-tracking/${id}/submit`);
      toast.success("Custom tracking submitted for admin approval.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const disableApproved = async (id) => {
    if (!window.confirm("Disable this approved tracking snippet? It will stop firing immediately.")) return;
    try {
      await api.post(`/custom-tracking/${id}/disable`);
      toast.success("Tracking disabled.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Disable failed"); }
  };

  return (
    <div className="p-2" data-testid="mb-custom-tracking">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Code size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Custom Tracking
        </h1>
        <div className="label-mono ml-2">
          Request custom pixels & attribution scripts · admin-approval required
        </div>
        <div className="ml-auto flex items-center gap-2">
          <HelpLink slug="mb-custom-tracking" label="Custom tracking guide" testId="help-link-mb-custom-tracking" />
          <button
            className="border border-black bg-black text-white px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-white hover:text-black transition-colors"
            onClick={openCreate}
            data-testid="mb-tracking-new-btn"
          >
            <Plus size={14} className="inline mr-1" /> Request Custom Tracking
          </button>
        </div>
      </div>

      {killSwitch && (
        <div
          className="border-2 border-red-700 bg-red-50 p-3 mb-4 flex items-start gap-2"
          data-testid="mb-tracking-kill-switch-banner"
        >
          <Warning size={20} weight="fill" className="text-red-700 mt-0.5" />
          <div>
            <div className="font-bold text-red-900 uppercase tracking-widest text-xs">
              Global custom-tracking kill switch is ON
            </div>
            <div className="text-sm text-red-900 mt-0.5">
              An admin has temporarily disabled every custom tracking snippet
              platform-wide. Approved code will not fire until the kill switch is
              cleared. Existing Meta / Google Ads / GA4 / TikTok structured
              pixels are unaffected.
            </div>
          </div>
        </div>
      )}

      <div className="mb-3 flex gap-2 flex-wrap" data-testid="mb-tracking-filters">
        {["all", "draft", "pending", "approved", "rejected", "disabled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`mb-tracking-filter-${s}`}
            className={`border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest ${
              statusFilter === s ? "bg-black text-white" : "hover:bg-zinc-100"
            }`}
          >{s === "all" ? "All" : STATUS_BADGE[s]?.label}</button>
        ))}
      </div>

      {loading && <div className="label-mono">Loading…</div>}

      {!loading && requests.length === 0 && (
        <div className="brut-card p-6 text-center" data-testid="mb-tracking-empty">
          <div className="label-mono">No custom tracking requests yet</div>
          <div className="text-sm mt-1 text-zinc-600">
            Click &quot;Request Custom Tracking&quot; above to add a snippet for admin review.
          </div>
        </div>
      )}

      {!loading && requests.length > 0 && (
        <>
        <div className="hidden md:block brut-card overflow-x-auto">
        <table className="w-full border-collapse text-sm min-w-[860px]" data-testid="mb-tracking-table">
          <thead className="bg-zinc-100 border-b border-black">
            <tr className="text-left uppercase text-[10px] tracking-widest">
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Provider</th>
              <th className="py-2 px-3">Landing Page</th>
              <th className="py-2 px-3">Placement</th>
              <th className="py-2 px-3">Trigger</th>
              <th className="py-2 px-3">Version</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
              const Icon = badge.icon;
              return (
                <tr key={r.id} className="border-t border-zinc-200" data-testid={`mb-tracking-row-${r.id}`}>
                  <td className="py-2 px-3 font-semibold">{r.tracking_name}</td>
                  <td className="py-2 px-3">{r.provider_name}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">{r.landing_page_slug}</td>
                  <td className="py-2 px-3 text-[11px] uppercase">{r.placement.replace("_"," ")}</td>
                  <td className="py-2 px-3 text-[11px] uppercase">{r.trigger.replace("_"," ")}</td>
                  <td className="py-2 px-3 text-[11px]">v{r.version}</td>
                  <td className="py-2 px-3">
                    <span
                      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}
                      data-testid={`mb-tracking-badge-${r.id}`}
                    >
                      <Icon size={11} weight="fill" />{badge.label}
                    </span>
                    <TrackingHealthBadge row={r} testId={`mb-tracking-health-${r.id}`} />
                    {r.status === "rejected" && r.review_note && (
                      <div
                        className="text-[11px] mt-1 text-red-800 italic"
                        data-testid={`mb-tracking-rejection-note-${r.id}`}
                      >
                        Note: {r.review_note}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-1 flex-wrap">
                      <button
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                        onClick={() => setReviewingId(r.id)}
                        data-testid={`mb-tracking-view-${r.id}`}
                      >View</button>
                      <button
                        className="border border-emerald-800 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800 hover:text-white"
                        onClick={() => testRow(r.id)}
                        data-testid={`mb-tracking-test-${r.id}`}
                      >Test my code</button>
                      {(r.status === "draft" || r.status === "pending" || r.status === "rejected") && (
                        <button
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                          onClick={() => openEdit(r)}
                          data-testid={`mb-tracking-edit-${r.id}`}
                        >Edit</button>
                      )}
                      {(r.status === "draft" || r.status === "rejected") && (
                        <button
                          className="border border-emerald-800 bg-emerald-100 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800 hover:text-white"
                          onClick={() => submitFor(r.id)}
                          data-testid={`mb-tracking-submit-${r.id}`}
                        >Submit for approval</button>
                      )}
                      {(r.status === "approved" || r.status === "disabled") && (
                        <button
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                          onClick={() => openEdit({ ...r, _versioning: true })}
                          data-testid={`mb-tracking-version-${r.id}`}
                        >Create new version</button>
                      )}
                      {r.status === "approved" && r.enabled && (
                        <button
                          className="border border-red-700 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white"
                          onClick={() => disableApproved(r.id)}
                          data-testid={`mb-tracking-disable-${r.id}`}
                        >Disable</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-2" data-testid="mb-tracking-cards">
          {requests.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
            const Icon = badge.icon;
            return (
              <div key={r.id} className="border border-black bg-white p-3" data-testid={`mb-tracking-card-${r.id}`}>
                <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                  <div className="font-bold text-sm truncate flex-1">{r.tracking_name}</div>
                  <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[9px] uppercase tracking-widest ${badge.cls}`}
                    data-testid={`mb-tracking-badge-mobile-${r.id}`}>
                    <Icon size={10} weight="fill" />{badge.label}
                  </span>
                </div>
                <div className="text-xs text-zinc-700 mt-1">{r.provider_name}</div>
                <div className="text-[11px] font-mono text-zinc-600 mt-0.5">/lp/{r.landing_page_slug} · v{r.version}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                  {r.placement.replace("_"," ")} · {r.trigger.replace("_"," ")}
                </div>
                <TrackingHealthBadge row={r} testId={`mb-tracking-health-mobile-${r.id}`} />
                {r.status === "rejected" && r.review_note && (
                  <div className="text-[11px] mt-2 text-red-800 italic" data-testid={`mb-tracking-rejection-note-mobile-${r.id}`}>
                    Note: {r.review_note}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  <button className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                    onClick={() => setReviewingId(r.id)}
                    data-testid={`mb-tracking-view-mobile-${r.id}`}>View</button>
                  <button className="border border-emerald-800 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest"
                    onClick={() => testRow(r.id)}
                    data-testid={`mb-tracking-test-mobile-${r.id}`}>Test my code</button>
                  {(r.status === "draft" || r.status === "pending" || r.status === "rejected") && (
                    <button className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                      onClick={() => openEdit(r)}
                      data-testid={`mb-tracking-edit-mobile-${r.id}`}>Edit</button>
                  )}
                  {(r.status === "draft" || r.status === "rejected") && (
                    <button className="border border-emerald-800 bg-emerald-100 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest"
                      onClick={() => submitFor(r.id)}
                      data-testid={`mb-tracking-submit-mobile-${r.id}`}>Submit</button>
                  )}
                  {r.status === "approved" && r.enabled && (
                    <button className="border border-red-700 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest"
                      onClick={() => disableApproved(r.id)}
                      data-testid={`mb-tracking-disable-mobile-${r.id}`}>Disable</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {showForm && (
        <TrackingFormModal
          initial={editing}
          pages={pages}
          onClose={closeForm}
          onSaved={() => { closeForm(); load(); }}
        />
      )}

      {reviewingId && (
        <ReviewModal
          id={reviewingId}
          onClose={() => setReviewingId(null)}
        />
      )}

      {testing && <TrackingTestModal snippet={testing} onClose={() => setTesting(null)} />}
    </div>
  );
}


function TrackingFormModal({ initial, pages, onClose, onSaved }) {
  const versioning = initial?._versioning;
  const [form, setForm] = useState(() => ({
    landing_page_id: initial?.landing_page_id || pages[0]?.id || "",
    provider_name: initial?.provider_name || "",
    tracking_name: initial?.tracking_name || "",
    tracking_type: initial?.tracking_type || "",
    placement: initial?.placement || "body_end",
    trigger: initial?.trigger || "page_view",
    custom_event_name: initial?.custom_event_name || "",
    code_snippet: initial?.code_snippet || "",
    notes_for_admin: initial?.notes_for_admin || "",
  }));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const isEdit = Boolean(initial && !versioning);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyExample = () => {
    if (form.code_snippet.trim() && !window.confirm("Replace the code you've pasted with the example?")) return;
    setForm((f) => ({
      ...f,
      code_snippet: TRACKING_EXAMPLE,
      trigger: "all_events",
      placement: f.placement === "thank_you_only" ? "body_end" : f.placement,
    }));
    toast.success("Example added and trigger set to All events");
  };

  const save = async (submitAfter) => {
    if (!form.landing_page_id) return toast.error("Choose a landing page");
    if (!form.provider_name.trim() || !form.tracking_name.trim()) return toast.error("Name + provider required");
    if (!form.code_snippet.trim()) return toast.error("Paste the tracking code");
    setSaving(true);
    try {
      let created;
      if (versioning) {
        const r = await api.post(`/custom-tracking/${initial.id}/version`, form);
        created = r.data;
        toast.success("New version submitted for admin approval.");
      } else if (isEdit) {
        const r = await api.patch(`/custom-tracking/${initial.id}`, form);
        created = r.data;
        toast.success("Saved.");
        if (submitAfter && (created.status === "draft" || created.status === "rejected")) {
          await api.post(`/custom-tracking/${created.id}/submit`);
          toast.success("Submitted for admin approval.");
        }
      } else {
        const r = await api.post("/custom-tracking", form);
        created = r.data;
        if (submitAfter) {
          await api.post(`/custom-tracking/${created.id}/submit`);
          toast.success("Custom tracking submitted for admin approval.");
        } else {
          toast.success("Draft saved.");
        }
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-6"
         data-testid="mb-tracking-form-modal">
      <div className="bg-white border-2 border-black w-full max-w-3xl mt-8 mb-8">
        <div className="border-b border-black bg-black text-white px-5 py-3 flex items-center gap-3">
          <div className="font-chivo font-black text-xl uppercase">
            {versioning ? "Create new version" : (isEdit ? "Edit tracking request" : "Request custom tracking")}
          </div>
          <button className="ml-auto text-xs uppercase tracking-widest" onClick={onClose} data-testid="mb-tracking-form-close">Close</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Tracking name">
              <input className="w-full border border-black px-2 py-1.5" value={form.tracking_name}
                     onChange={(e) => set("tracking_name", e.target.value)}
                     data-testid="mb-tracking-field-name" />
            </Field>
            <Field label="Provider (RedTrack, Voluum, Hyros, …)">
              <input className="w-full border border-black px-2 py-1.5" value={form.provider_name}
                     onChange={(e) => set("provider_name", e.target.value)}
                     data-testid="mb-tracking-field-provider" />
            </Field>
            <Field label="Landing page">
              <select className="w-full border border-black px-2 py-1.5" value={form.landing_page_id}
                      onChange={(e) => set("landing_page_id", e.target.value)}
                      disabled={versioning}
                      data-testid="mb-tracking-field-page">
                {pages.map((p) => <option key={p.id} value={p.id}>{p.firm_name || p.slug} · /lp/{p.slug}</option>)}
              </select>
            </Field>
            <Field label="Placement">
              <select className="w-full border border-black px-2 py-1.5" value={form.placement}
                      onChange={(e) => set("placement", e.target.value)}
                      data-testid="mb-tracking-field-placement">
                {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Trigger">
              <select className="w-full border border-black px-2 py-1.5" value={form.trigger}
                      onChange={(e) => set("trigger", e.target.value)}
                      data-testid="mb-tracking-field-trigger">
                {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            {form.trigger === "custom_event" && (
              <Field label="Custom internal event name">
                <input className="w-full border border-black px-2 py-1.5" value={form.custom_event_name}
                       onChange={(e) => set("custom_event_name", e.target.value)}
                       data-testid="mb-tracking-field-event" />
              </Field>
            )}
          </div>
          <TrackingGuideCard onUse={applyExample} />
          <Field label="Code / snippet">
            <textarea
              className="w-full border border-black px-2 py-1.5 font-mono text-[12px]"
              rows={12}
              value={form.code_snippet}
              onChange={(e) => set("code_snippet", e.target.value)}
              placeholder='<script src="https://track.example.com/px.js" async></script>'
              data-testid="mb-tracking-field-code"
            />
          </Field>
          <Field label="Notes for admin (optional)">
            <textarea
              className="w-full border border-black px-2 py-1.5"
              rows={3}
              value={form.notes_for_admin}
              onChange={(e) => set("notes_for_admin", e.target.value)}
              data-testid="mb-tracking-field-notes"
            />
          </Field>
          <div className="text-[11px] text-zinc-600 border-l-2 border-emerald-700 pl-3 bg-emerald-50 py-2">
            <b>How custom tracking runs on LeadFlow:</b> approved snippets execute
            inside an isolated <code className="font-mono">&lt;iframe sandbox=&quot;allow-scripts&quot;&gt;</code> with
            a null opaque origin. Your code CANNOT read the visitor&apos;s name / phone /
            email / consent state, LeadFlow cookies, buyer pricing, cost, margin,
            or routing data. It receives only PII-safe context (slug, page id,
            variant, sub_id, utm_*, session id, event name) via one-way
            <code className="font-mono">postMessage</code>. Runs on <b>live production traffic
            only</b> — preview links + sample pages never fire your snippet.
            <div className="mt-2" data-testid="mb-tracking-api-help">
              <b>In your code:</b> <code className="font-mono">CustomTracking.context</code> has <code className="font-mono">sub_id</code>,{" "}
              <code className="font-mono">session_id</code>, <code className="font-mono">utm_*</code> and the page as soon as it loads.
              Use <code className="font-mono">CustomTracking.on(&quot;form_start&quot; | &quot;lead_submit&quot; | &quot;thank_you&quot;, fn)</code> to
              hear each moment. Pick the <b>All events</b> trigger so one code runs on the landing and thank-you pages
              and hears all three. Press <b>Test my code</b> to try it before you submit.
            </div>
          </div>
        </div>
        <div className="border-t border-black px-5 py-3 flex gap-2 justify-end flex-wrap">
          <button onClick={() => (form.code_snippet.trim() ? setTesting(true) : toast.error("Paste the tracking code first"))}
                  className="mr-auto border border-emerald-800 text-emerald-900 px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-800 hover:text-white"
                  data-testid="mb-tracking-form-test">Test my code</button>
          <button onClick={onClose} className="border border-black px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-zinc-100"
                  data-testid="mb-tracking-form-cancel">Cancel</button>
          {!versioning && (
            <button onClick={() => save(false)} disabled={saving}
                    className="border border-black px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50"
                    data-testid="mb-tracking-form-draft">Save as draft</button>
          )}
          <button onClick={() => save(true)} disabled={saving}
                  className="border-2 border-emerald-800 bg-emerald-800 text-white px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-900 disabled:opacity-50"
                  data-testid="mb-tracking-form-submit">
            {versioning ? "Submit new version" : "Submit for approval"}
          </button>
        </div>
        {testing && (
          <TrackingTestModal
            snippet={{ ...form, landing_page_slug: pages.find((p) => p.id === form.landing_page_id)?.slug }}
            onClose={() => setTesting(false)}
          />
        )}
      </div>
    </div>
  );
}


function ReviewModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/custom-tracking/mine/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);
  const req = data?.request;
  const s = data?.scan;
  const badge = req && (STATUS_BADGE[req.status] || STATUS_BADGE.draft);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-6"
         data-testid="mb-tracking-view-modal">
      <div className="bg-white border-2 border-black w-full max-w-3xl mt-8 mb-8">
        <div className="border-b border-black bg-black text-white px-5 py-3 flex items-center gap-3">
          <div className="font-chivo font-black text-xl uppercase truncate">
            {req?.tracking_name || "Loading…"}
          </div>
          {badge && <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}>
            {badge.label}
          </span>}
          <button className="ml-auto text-xs uppercase tracking-widest" onClick={onClose} data-testid="mb-tracking-view-close">Close</button>
        </div>
        {loading && <div className="p-6 label-mono">Loading…</div>}
        {req && !loading && (
          <div className="p-5 space-y-3">
            <MetaRow k="Provider" v={req.provider_name} />
            <MetaRow k="Landing page" v={`/lp/${req.landing_page_slug}`} />
            <MetaRow k="Placement" v={req.placement.replace("_"," ")} />
            <MetaRow k="Trigger" v={req.trigger.replace("_"," ")} />
            <MetaRow k="Version" v={`v${req.version}`} />
            {req.review_note && <MetaRow k={req.status === "rejected" ? "Admin note" : "Review note"} v={req.review_note} highlight={req.status === "rejected"} />}
            {req.status === "approved" && (
              <div className="text-[11px] text-emerald-800">
                Approved by {req.approved_by_name} on {new Date(req.approved_at).toLocaleString()}
                {req.enabled ? " · currently firing on live traffic" : " · currently disabled"}
              </div>
            )}
            <div>
              <div className="label-mono mb-1">Code (read-only)</div>
              <pre className="font-mono text-[11px] bg-zinc-100 border border-black p-3 max-h-72 overflow-auto whitespace-pre-wrap"
                   data-testid="mb-tracking-view-code">{req.code_snippet || "(empty)"}</pre>
            </div>
            {s && (
              <div className="border border-zinc-400 p-3 bg-zinc-50 text-[12px]">
                <div className="label-mono mb-1">Safety scan (advisory)</div>
                <div className="uppercase font-semibold text-xs mb-1">{s.verdict.toUpperCase()}</div>
                {s.hard_blocks.length > 0 && (
                  <div className="text-red-800">Hard blocks: {s.hard_blocks.join(", ")}</div>
                )}
                {s.warnings.length > 0 && (
                  <div className="text-amber-800 mt-1">Warnings: {s.warnings.join(", ")}</div>
                )}
                {s.domains.length > 0 && (
                  <div className="mt-1">Domains contacted: {s.domains.join(", ")}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block text-[11px] uppercase tracking-widest font-semibold">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}


function MetaRow({ k, v, highlight }) {
  return (
    <div className={`flex gap-3 text-sm ${highlight ? "bg-red-50 border-l-2 border-red-700 pl-3 py-1" : ""}`}>
      <div className="label-mono w-28 shrink-0">{k}</div>
      <div className="font-mono text-[12px] break-all">{v}</div>
    </div>
  );
}
